/**
 * HomePageForSupplier Component
 * 협력사 전용 홈 페이지 (배민 SLA 관리)
 */

import React, { useRef, useEffect, useCallback, useState, TouchEvent, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import BottomNavigation from '../../../core/components/BottomNavigation';
import BalanceDisplay from './BalanceDisplay';
import CustomSelect, { SelectOption } from '../../../core/components/CustomSelect';
import { useHomePageForSupplier } from '../hooks/useHomePageForSupplier';
import { useDeferredLoading } from '../../../core/hooks/useDeferredLoading';
import { usePullToRefresh } from '../../../core/hooks/usePullToRefresh';
import { AutoRefreshInterval, RiderData, SupplierTabType } from '../types/homePageForSupplier';
import NoticePopupModal from '../../system/notice/components/NoticePopupModal';
import { getMobileUnreadPopupNotices } from '../../system/notice/api/noticeApi';
import { NoticeResult } from '../../system/notice/types/notice.types';
import { useMaintenanceContext } from '../../system/maintenance/contexts/MaintenanceContext';
import MaintenanceScreen from '../../system/maintenance/components/MaintenanceScreen';
import styles from '../styles/HomePageForSupplier.module.css';

type DotSize = 'active' | 'normal' | 'small';

function getPaginationDots(current: number, total: number, maxVisible = 5): { index: number; size: DotSize }[] {
    if (total <= maxVisible) {
        return Array.from({ length: total }, (_, i) => ({
            index: i,
            size: (i === current ? 'active' : 'normal') as DotSize,
        }));
    }
    let start = current - Math.floor(maxVisible / 2);
    let end = start + maxVisible - 1;
    if (start < 0) { start = 0; end = maxVisible - 1; }
    if (end >= total) { end = total - 1; start = total - maxVisible; }
    return Array.from({ length: maxVisible }, (_, i) => {
        const pageIndex = start + i;
        let size: DotSize;
        if (pageIndex === current) size = 'active';
        else if ((i === 0 && start > 0) || (i === maxVisible - 1 && end < total - 1)) size = 'small';
        else size = 'normal';
        return { index: pageIndex, size };
    });
}

const HomePageForSupplier: React.FC = () => {
    const navigate = useNavigate();
    const { activeMaintenance, isMaintenance, loading: maintenanceLoading } = useMaintenanceContext();
    const {
        // 사용자 정보
        supplierInfo,

        // 탭 상태
        activeTab,
        handleTabChange,

        // 자동 갱신 상태
        autoRefreshInterval,
        showAutoRefreshSelect,
        lastUpdate,
        handleAutoRefreshToggle,
        handleAutoRefreshChange,

        // 옵션 목록
        dateOptions,
        todayDayOfWeek,
        autoRefreshOptions,
        setCount,

        // 주간 현황 탭
        weeklyStats,
        adjustedSlotStats,
        dailyData,
        fetchWeeklyStatus,
        weeklyStatusOptions,
        selectedWeeklyOption,
        handleWeeklyOptionChange,

        // 금일 달성현황 탭
        selectedDate,
        dailyStats,
        timeSlots,
        slotsInitialized,
        handleSelectedDateChange,
        fetchDailyStatus,

        // 라이더현황 탭 (당일)
        riderStats,
        riders,
        riderSlotNames,
        ridersInitialized,
        fetchRiderStatus,

        // 주간 라이더현황
        weeklyRiderStats,
        weeklyRiders,
        fetchWeeklyRiderStatus,

        // 공통 상태
        isLoading: rawIsLoading,
        error,
    } = useHomePageForSupplier();

    // 지연 로딩 적용 (200ms 이상 로딩 시에만 로딩 UI 표시)
    const isLoading = useDeferredLoading(rawIsLoading, 200);

    // 컴포넌트 마운트 시 데이터 로드
    useEffect(() => {
        fetchDailyStatus();
        fetchRiderStatus();
        fetchWeeklyStatus();
        fetchWeeklyRiderStatus();
    }, []);

    // 라이더 모달 상태
    const [selectedRider, setSelectedRider] = useState<RiderData | null>(null);
    const [isRiderModalOpen, setIsRiderModalOpen] = useState(false);

    // 공지사항 팝업 상태
    const [popupNotices, setPopupNotices] = useState<NoticeResult[]>([]);
    const [currentNoticeIndex, setCurrentNoticeIndex] = useState(0);

    // 점검 화면 확인 여부 (세션당 한 번만 표시)
    const [maintenanceAcknowledged, setMaintenanceAcknowledged] = useState(() => {
        const acknowledged = sessionStorage.getItem('maintenance_acknowledged');
        const acknowledgedSeqNo = sessionStorage.getItem('maintenance_acknowledged_seqno');
        // 점검 ID가 변경되었으면 확인 상태 초기화
        if (activeMaintenance && acknowledgedSeqNo !== String(activeMaintenance.seqNo)) {
            sessionStorage.removeItem('maintenance_acknowledged');
            return false;
        }
        return acknowledged === 'true';
    });

    // 점검 상태가 변경되면 확인 상태 초기화
    useEffect(() => {
        if (!isMaintenance) {
            sessionStorage.removeItem('maintenance_acknowledged');
            sessionStorage.removeItem('maintenance_acknowledged_seqno');
            setMaintenanceAcknowledged(false);
        } else if (activeMaintenance) {
            // 점검 ID가 변경되었는지 확인
            const acknowledgedSeqNo = sessionStorage.getItem('maintenance_acknowledged_seqno');
            if (acknowledgedSeqNo && acknowledgedSeqNo !== String(activeMaintenance.seqNo)) {
                sessionStorage.removeItem('maintenance_acknowledged');
                sessionStorage.removeItem('maintenance_acknowledged_seqno');
                setMaintenanceAcknowledged(false);
            }
        }
    }, [isMaintenance, activeMaintenance]);

    // 오늘 하루 보지 않기 처리된 공지 ID 가져오기
    const getHiddenNoticeIds = (): number[] => {
        const today = new Date().toISOString().split('T')[0];
        const stored = localStorage.getItem('hiddenNotices');
        if (!stored) return [];
        try {
            const data = JSON.parse(stored);
            if (data.date === today) {
                return data.ids || [];
            }
            return [];
        } catch {
            return [];
        }
    };

    // 오늘 하루 보지 않기 저장
    const hideNoticeForToday = (noticeId: number) => {
        const today = new Date().toISOString().split('T')[0];
        const hiddenIds = getHiddenNoticeIds();
        if (!hiddenIds.includes(noticeId)) {
            hiddenIds.push(noticeId);
        }
        localStorage.setItem('hiddenNotices', JSON.stringify({ date: today, ids: hiddenIds }));
    };

    // 공지사항 팝업 로드
    useEffect(() => {
        const fetchPopupNotices = async () => {
            try {
                const response = await getMobileUnreadPopupNotices();
                const hiddenIds = getHiddenNoticeIds();
                const filteredNotices = (response.notices || []).filter(
                    (notice) => !hiddenIds.includes(notice.noticeId)
                );
                setPopupNotices(filteredNotices);
            } catch (error) {
                console.error('팝업 공지 조회 실패:', error);
            }
        };
        fetchPopupNotices();
    }, []);

    // 공지사항 팝업 닫기
    const handleCloseNotice = () => {
        if (currentNoticeIndex < popupNotices.length - 1) {
            setCurrentNoticeIndex(prev => prev + 1);
        } else {
            setPopupNotices([]);
            setCurrentNoticeIndex(0);
        }
    };

    // 오늘 하루 보지 않기
    const handleHideForToday = (noticeId: number) => {
        hideNoticeForToday(noticeId);
        handleCloseNotice();
    };

    // SelectOption 형식으로 변환
    const dateSelectOptions: SelectOption[] = useMemo(() =>
        dateOptions.map(opt => ({ value: opt.value, label: opt.label })),
        [dateOptions]
    );

    const weeklySelectOptions: SelectOption[] = useMemo(() =>
        weeklyStatusOptions.map(opt => ({ value: opt.value, label: opt.label })),
        [weeklyStatusOptions]
    );

    // 이름 툴팁 상태 (long press)
    const [nameTooltip, setNameTooltip] = useState<{ visible: boolean; name: string; x: number; y: number }>({
        visible: false,
        name: '',
        x: 0,
        y: 0,
    });
    const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);

    // 당일 라이더 페이지네이션 상태
    const [riderPage, setRiderPage] = useState(0);
    const RIDERS_PER_PAGE = 10;
    const totalRiderPages = Math.ceil(riders.length / RIDERS_PER_PAGE);
    const paginatedRiders = riders.slice(riderPage * RIDERS_PER_PAGE, (riderPage + 1) * RIDERS_PER_PAGE);

    // 주간 라이더 페이지네이션 상태
    const [weeklyRiderPage, setWeeklyRiderPage] = useState(0);
    const totalWeeklyRiderPages = Math.ceil(weeklyRiders.length / RIDERS_PER_PAGE);
    const paginatedWeeklyRiders = weeklyRiders.slice(weeklyRiderPage * RIDERS_PER_PAGE, (weeklyRiderPage + 1) * RIDERS_PER_PAGE);

    // 스와이프 관련 상태
    const touchStartX = useRef(0);
    const touchEndX = useRef(0);
    const riderTableRef = useRef<HTMLDivElement>(null);
    const weeklyRiderTableRef = useRef<HTMLDivElement>(null);

    const handleRiderRowClick = (rider: RiderData) => {
        setSelectedRider(rider);
        setIsRiderModalOpen(true);
    };

    const closeRiderModal = () => {
        setIsRiderModalOpen(false);
        setSelectedRider(null);
    };

    // 이름 Long Press 핸들러
    const handleNameTouchStart = (e: React.TouchEvent<HTMLSpanElement>, name: string) => {
        const touch = e.touches[0];
        longPressTimerRef.current = setTimeout(() => {
            setNameTooltip({
                visible: true,
                name,
                x: touch.clientX,
                y: touch.clientY - 50,
            });
        }, 1000); // 1초
    };

    const handleNameTouchEnd = () => {
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
    };

    const hideNameTooltip = () => {
        setNameTooltip(prev => ({ ...prev, visible: false }));
    };

    const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
        touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchMove = (e: TouchEvent<HTMLDivElement>) => {
        touchEndX.current = e.touches[0].clientX;
    }

    const handleTouchEnd = () => {
        const swipeThreshold = 50;
        const diff = touchStartX.current - touchEndX.current;

        if (Math.abs(diff) > swipeThreshold) {
            if (diff > 0 && riderPage < totalRiderPages - 1) {
                // 왼쪽으로 스와이프 -> 다음 페이지
                setRiderPage(prev => prev + 1);
            } else if (diff < 0 && riderPage > 0) {
                // 오른쪽으로 스와이프 -> 이전 페이지
                setRiderPage(prev => prev - 1);
            }
        }
    };

    // 주간 라이더 스와이프 핸들러
    const handleWeeklyTouchEnd = () => {
        const swipeThreshold = 50;
        const diff = touchStartX.current - touchEndX.current;

        if (Math.abs(diff) > swipeThreshold) {
            if (diff > 0 && weeklyRiderPage < totalWeeklyRiderPages - 1) {
                // 왼쪽으로 스와이프 -> 다음 페이지
                setWeeklyRiderPage(prev => prev + 1);
            } else if (diff < 0 && weeklyRiderPage > 0) {
                // 오른쪽으로 스와이프 -> 이전 페이지
                setWeeklyRiderPage(prev => prev - 1);
            }
        }
    };

    // 섹션 refs
    const containerRef = useRef<HTMLDivElement>(null);
    const weeklyScoreRef = useRef<HTMLDivElement>(null);
    const dailyStatusRef = useRef<HTMLDivElement>(null);

    // Pull-to-refresh
    const handleRefresh = useCallback(async () => {
        await Promise.all([
            fetchDailyStatus(),
            fetchRiderStatus(),
            fetchWeeklyStatus(),
            fetchWeeklyRiderStatus(),
        ]);
    }, [fetchDailyStatus, fetchRiderStatus, fetchWeeklyStatus, fetchWeeklyRiderStatus]);

    const { pullDistance, isRefreshing, threshold } = usePullToRefresh(handleRefresh, containerRef);

    // 스크롤 중인지 확인 (탭 클릭으로 인한 스크롤 시 스크롤 감지 비활성화)
    const isScrollingByClick = useRef(false);

    // 탭 클릭 시 해당 섹션으로 스크롤
    const handleTabClick = useCallback((tab: SupplierTabType) => {
        const refMap: Partial<{ [key in SupplierTabType]: React.RefObject<HTMLDivElement | null> }> = {
            weeklyScore: weeklyScoreRef,
            dailyStatus: dailyStatusRef,
            riderStatus: dailyStatusRef,
        };

        const targetRef = refMap[tab];
        if (targetRef && targetRef.current && containerRef.current) {
            isScrollingByClick.current = true;
            handleTabChange(tab);

            const headerHeight = 190;
            const targetPosition = targetRef.current.offsetTop - headerHeight;

            containerRef.current.scrollTo({
                top: targetPosition,
                behavior: 'smooth',
            });

            // 스크롤 애니메이션 완료 후 플래그 해제
            setTimeout(() => {
                isScrollingByClick.current = false;
            }, 500);
        }
    }, [handleTabChange]);

    // 스크롤 이벤트 핸들러 - 현재 보이는 섹션에 따라 탭 활성화
    const handleScroll = useCallback(() => {
        if (isScrollingByClick.current || !containerRef.current) return;

        const container = containerRef.current;
        const scrollTop = container.scrollTop;
        const headerHeight = 180;
        const sections: { ref: React.RefObject<HTMLDivElement | null>; tab: SupplierTabType }[] = [
            { ref: dailyStatusRef, tab: 'dailyStatus' },
            { ref: weeklyScoreRef, tab: 'weeklyScore' },
        ];

        // 각 섹션의 위치를 확인하여 현재 보이는 섹션 찾기
        for (let i = sections.length - 1; i >= 0; i--) {
            const section = sections[i];
            if (section.ref.current) {
                const sectionTop = section.ref.current.offsetTop - headerHeight - 60;
                if (scrollTop >= sectionTop) {
                    if (activeTab !== section.tab) {
                        handleTabChange(section.tab);
                    }
                    break;
                }
            }
        }
    }, [activeTab, handleTabChange]);

    // 스크롤 이벤트 리스너 등록
    useEffect(() => {
        const container = containerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            return () => container.removeEventListener('scroll', handleScroll);
        }
    }, [handleScroll]);

    // 탭 메뉴 렌더링
    const renderTabs = () => (
        <div className={styles['supplier-tabs']}>
            <button
                className={`${styles['tab-btn']} ${activeTab === 'dailyStatus' ? styles['active'] : ''}`}
                onClick={() => handleTabClick('dailyStatus')}
            >
                당일 현황
            </button>
            <button
                className={`${styles['tab-btn']} ${activeTab === 'weeklyScore' ? styles['active'] : ''}`}
                onClick={() => handleTabClick('weeklyScore')}
            >
                주간 현황
            </button>
        </div>
    );

    // 당일 현황 섹션 렌더링
    const renderDailyStatusSection = () => (
        <div ref={dailyStatusRef} className={styles['section']}>
            <h2 className={styles['section-title']}>당일 현황</h2>
            <div className={styles['section-content']}>
                {/* 조회일자 선택 */}
                <div className={styles['date-selector-section']}>
                    <CustomSelect
                        label="조회일자"
                        options={dateSelectOptions}
                        value={selectedDate}
                        onChange={handleSelectedDateChange}
                    />
                </div>

                {/* 당일 통계 */}
                {(() => {
                    const activeSlots = timeSlots.filter(sl => sl.target > 0);
                    const totalTarget = activeSlots.reduce((s, sl) => s + sl.target, 0);
                    const totalCurrent = activeSlots.reduce((s, sl) => s + sl.current, 0);
                    const dailySlotRate = totalTarget > 0
                        ? Math.floor((totalCurrent / totalTarget) * 1000) / 10
                        : 0;
                    return (
                        <div className={styles['daily-stats-cards']}>
                            <div className={`${styles['daily-stat-card']} ${styles['stat-blue']}`}>
                                <div className={styles['daily-stat-card-label']}>총 완료</div>
                                <div className={styles['daily-stat-card-value']}>{dailyStats.completedCount}</div>
                            </div>
                            <div className={`${styles['daily-stat-card']} ${styles['stat-green']}`}>
                                <div className={styles['daily-stat-card-label']}>수락률</div>
                                <div className={styles['daily-stat-card-value']}>{dailyStats.acceptanceRate}%</div>
                            </div>
                            <div className={`${styles['daily-stat-card']} ${styles['stat-red']}`}>
                                <div className={styles['daily-stat-card-label']}>거절</div>
                                <div className={styles['daily-stat-card-value']}>{dailyStats.rejectedCount}</div>
                            </div>
                            <div className={`${styles['daily-stat-card']} ${styles['stat-green']}`}>
                                <div className={styles['daily-stat-card-label']}>슬롯 수행률</div>
                                <div className={styles['daily-stat-card-value']}>
                                    {totalTarget > 0 ? `${dailySlotRate}%` : '-'}
                                </div>
                                {totalTarget > 0 && (
                                    <div className={styles['daily-stat-card-sub']}>{totalCurrent}/{totalTarget}건</div>
                                )}
                            </div>
                        </div>
                    );
                })()}

                {/* 업데이트 시간 */}
                <div className={styles['time-slots-update']}>업데이트 {lastUpdate}</div>

                {/* 시간대별 슬롯 */}
                <div className={styles['time-slots-container']}>
                    <div className={styles['time-slots-header-row']}>
                        <span className={styles['time-slots-title']}>시간대 슬롯</span>
                        <span className={styles['time-slots-meta']}>{setCount}</span>
                    </div>
                    {!slotsInitialized ? (
                        <div className={styles['pull-indicator']} style={{ height: 56 }}>
                            <div className={`${styles['pull-icon']} ${styles['pull-icon-spinning']}`}>↻</div>
                            <span className={styles['pull-text']}>불러오는 중...</span>
                        </div>
                    ) : (
                    <div className={styles['time-slots-list']}>
                        {timeSlots.map((slot) => {
                            const progressPct = slot.target > 0
                                ? Math.min((slot.current / slot.target) * 100, 100)
                                : 0;
                            const rateNum = parseFloat(String(slot.rate));
                            const isNoTarget = slot.target === 0;

                            return (
                                <div key={slot.id} className={styles['time-slot-item']}>
                                    <div className={styles['time-slot-main']} style={isNoTarget ? { marginBottom: 0 } : undefined}>
                                        <div className={styles['time-slot-left']}>
                                            <span className={`${styles['time-slot-name']} ${isNoTarget ? styles['time-slot-name--dim'] : ''}`}>
                                                {slot.name}
                                            </span>
                                            <span className={styles['time-slot-range']}>{slot.timeRange}</span>
                                        </div>
                                        <div className={styles['time-slot-right']}>
                                            {isNoTarget ? (
                                                <span className={styles['time-slot-count-dim']}>{slot.current}</span>
                                            ) : (
                                                <>
                                                    <span className={styles['time-slot-count']}>{slot.current} / {slot.target}</span>
                                                    <span className={`${styles['time-slot-rate']} ${rateNum >= 100 ? styles['rate-over'] : ''}`}>
                                                        {slot.rate}%
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    {!isNoTarget && (
                                        <div className={styles['time-slot-progress']}>
                                            <div
                                                className={styles['time-slot-progress-bar']}
                                                style={{ width: `${progressPct}%` }}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    )}
                </div>

                {/* 당일 라이더 현황 */}
                <div className={styles['daily-rider-section']}>
                    <h3 className={styles['subsection-title']}>당일 라이더 현황</h3>
                    <div className={styles['rider-stats-cards']}>
                        <div className={`${styles['rider-stat-card']} ${styles['total']}`}>
                            <div className={styles['rider-stat-label']}>총 라이더</div>
                            <div className={styles['rider-stat-value']}>{riderStats.totalRiders}명</div>
                        </div>
                        <div className={`${styles['rider-stat-card']} ${styles['active']}`}>
                            <div className={styles['rider-stat-label']}>운행중</div>
                            <div className={styles['rider-stat-value']}>{riderStats.activeRiders}명</div>
                        </div>
                        <div className={`${styles['rider-stat-card']} ${styles['completed']}`}>
                            <div className={styles['rider-stat-label']}>운행종료</div>
                            <div className={styles['rider-stat-value']}>{riderStats.completedRiders}명</div>
                        </div>
                    </div>

                    {/* 당일 라이더 테이블 */}
                    <div
                        ref={riderTableRef}
                        className={styles['rider-table-container']}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                    >
                        {!ridersInitialized ? (
                            <div className={styles['pull-indicator']} style={{ height: 56 }}>
                                <div className={`${styles['pull-icon']} ${styles['pull-icon-spinning']}`}>↻</div>
                                <span className={styles['pull-text']}>불러오는 중...</span>
                            </div>
                        ) : error ? (
                            <div className={styles['rider-empty']} style={{ color: '#e74c3c' }}>
                                {error}
                            </div>
                        ) : riders.length === 0 ? (
                            <div className={styles['rider-empty']}>
                                등록된 라이더가 없습니다.
                            </div>
                        ) : (
                            <>
                                <div className={styles['rider-table-scroll']}>
                                    <table className={styles['rider-table']}>
                                        <thead>
                                        <tr>
                                            <th>이름</th>
                                            <th>운영상태</th>
                                            <th>전화번호</th>
                                            <th>수락건수</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {paginatedRiders.map((rider) => (
                                            <tr
                                                key={rider.id}
                                                onClick={() => handleRiderRowClick(rider)}
                                                className={styles['rider-table-row']}
                                            >
                                                <td>
                                                    <span
                                                        className={styles['rider-name']}
                                                        onTouchStart={(e) => handleNameTouchStart(e, rider.name)}
                                                        onTouchEnd={handleNameTouchEnd}
                                                        onTouchCancel={handleNameTouchEnd}
                                                    >
                                                        {rider.name}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`${styles['rider-status-badge']} ${styles[rider.status]}`}>
                                                        {rider.status === 'active' ? '운행중' : '운행종료'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <a
                                                        href={`tel:${rider.phoneNumber}`}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className={styles['phone-link']}
                                                    >
                                                        {rider.phoneNumber}
                                                    </a>
                                                </td>
                                                <td>{rider.completedCount}건</td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>
                                {/* 페이지 인디케이터 */}
                                {totalRiderPages > 1 && (
                                    <div className={styles['rider-pagination']}>
                                        <span className={styles['rider-pagination-text']}>
                                            {riderPage + 1} / {totalRiderPages}
                                        </span>
                                        <div className={styles['rider-pagination-dots']}>
                                            {getPaginationDots(riderPage, totalRiderPages).map(({ index, size}) => (
                                                <span
                                                    key={index}
                                                    className={`${styles['rider-pagination-dot']} ${styles[size]}`}
                                                />
                                            ))}
                                        </div>
                                        <span className={styles['rider-pagination-hint']}>← 스와이프하여 이동 →</span>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    // 주간 현황 섹션 렌더링
    const renderWeeklyScoreSection = () => (
        <div ref={weeklyScoreRef} className={styles['section']} style={{ marginBottom: '50px' }}>
            <h2 className={styles['section-title']}>주간 현황</h2>
            <div className={styles['section-content']}>
                {/* 주차 선택 */}
                <div className={styles['date-selector-section']}>
                    <CustomSelect
                        label="조회일자"
                        options={weeklySelectOptions}
                        value={selectedWeeklyOption.value}
                        onChange={handleWeeklyOptionChange}
                        placeholder="주차를 선택하세요"
                    />
                </div>

                {/* 주간 통계 카드 */}
                <div className={styles['daily-stats-cards']}>
                    <div className={`${styles['daily-stat-card']} ${styles['stat-blue']}`}>
                        <div className={styles['daily-stat-card-label']}>총 수행 건수</div>
                        <div className={styles['daily-stat-card-value']}>{weeklyStats.completedCount?.toLocaleString()}</div>
                    </div>
                    <div className={`${styles['daily-stat-card']} ${styles['stat-green']}`}>
                        <div className={styles['daily-stat-card-label']}>슬롯 수행률</div>
                        <div className={styles['daily-stat-card-value']}>
                            {weeklyStats.slotAchievementRate != null && !isNaN(weeklyStats.slotAchievementRate)
                                ? `${weeklyStats.slotAchievementRate.toFixed(1)}%`
                                : '-'}
                        </div>
                    </div>
                    <div className={`${styles['daily-stat-card']} ${styles['stat-green']}`}>
                        <div className={styles['daily-stat-card-label']}>슬롯 달성률</div>
                        <div className={styles['daily-stat-card-value']}>{adjustedSlotStats.achievementRate}%</div>
                        <div className={styles['daily-stat-card-sub']}>
                            {adjustedSlotStats.achievedSlots} / {adjustedSlotStats.totalSlots}개
                        </div>
                    </div>
                    <div className={`${styles['daily-stat-card']} ${styles['stat-green']}`}>
                        <div className={styles['daily-stat-card-label']}>주간 수락률</div>
                        <div className={styles['daily-stat-card-value']}>
                            {weeklyStats.acceptanceRate !== null && !isNaN(weeklyStats.acceptanceRate)
                                ? `${weeklyStats.acceptanceRate}%`
                                : '-'}
                        </div>
                    </div>
                </div>

                {/* 요일별 테이블 - 시간대별 정보 포함 */}
                {(() => {
                    const DEFAULT_SLOT_NAMES = ['아침점심피크', '오후논피크', '저녁피크', '야간'];
                    const weeklySlotNames = dailyData.find(d => d.slotNames.length > 0)?.slotNames ?? DEFAULT_SLOT_NAMES;
                    const emptySlots = weeklySlotNames.map(() => ({ rate: 0, count: 0, target: 0, isAchieved: false, hasTarget: true }));
                    return (
                        <div className={styles['weekly-table-container']}>
                            <table className={styles['weekly-table']}>
                                <thead>
                                <tr>
                                    <th className={styles['weekly-th-date']}>날짜</th>
                                    {weeklySlotNames.map((name) => (
                                        <th key={name}>{name}</th>
                                    ))}
                                </tr>
                                </thead>
                                <tbody>
                                {dailyData.map((day) => {
                                    const displaySlots = day.slots.length > 0 ? day.slots : emptySlots;
                                    return (
                                        <tr
                                            key={day.day}
                                            className={`${styles['weekly-table-row']} ${day.day === todayDayOfWeek ? styles['today-row'] : ''}`}
                                        >
                                            <td className={styles['weekly-td-date']}>
                                                {day.deliveryDate ? (
                                                    <>
                                                            <span className={styles['date-num']}>
                                                                {day.deliveryDate.slice(4, 6)}/{day.deliveryDate.slice(6, 8)}
                                                            </span>
                                                        <span className={styles['date-day']}>({day.label})</span>
                                                    </>
                                                ) : (
                                                    <span className={styles['date-day']}>{day.label}</span>
                                                )}
                                            </td>
                                            {displaySlots.map((slot, idx) => {
                                                const slotClass = !day.hasData
                                                    ? styles['no-data']
                                                    : !slot.hasTarget
                                                        ? styles['no-target']
                                                        : slot.isAchieved
                                                            ? styles['achieved']
                                                            : styles['not-achieved'];
                                                return (
                                                    <td key={idx} className={`${styles['weekly-td-slot']} ${slotClass}`}>
                                                        {day.hasData ? (
                                                            slot.hasTarget ? (
                                                                <>
                                                                    <span className={styles['slot-count']}>{slot.count}/{slot.target}건</span>
                                                                    <span className={styles['slot-rate']}>{slot.rate}%</span>
                                                                    <span className={styles['slot-status']}>{slot.isAchieved ? '달성' : '미달성'}</span>
                                                                </>
                                                            ) : (
                                                                <span className={styles['slot-count']}>{slot.count}건</span>
                                                            )
                                                        ) : '-'}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </div>
                    );
                })()}

                <div className={styles['data-notice']}>
                    ※ 데이터는 10~30분 간격으로 자동 갱신됩니다.
                </div>

                {/* 주간 라이더 현황 */}
                <div className={styles['weekly-rider-section']}>
                    <h3 className={styles['subsection-title']}>주간 라이더 현황</h3>
                    <div className={styles['rider-stats-cards']}>
                        <div className={`${styles['rider-stat-card']} ${styles['total']}`}>
                            <div className={styles['rider-stat-label']}>총 라이더</div>
                            <div className={styles['rider-stat-value']}>{weeklyRiderStats.totalRiders}명</div>
                        </div>
                        <div className={`${styles['rider-stat-card']} ${styles['active']}`}>
                            <div className={styles['rider-stat-label']}>운행중</div>
                            <div className={styles['rider-stat-value']}>{weeklyRiderStats.activeRiders}명</div>
                        </div>
                        <div className={`${styles['rider-stat-card']} ${styles['completed']}`}>
                            <div className={styles['rider-stat-label']}>운행종료</div>
                            <div className={styles['rider-stat-value']}>{weeklyRiderStats.completedRiders}명</div>
                        </div>
                    </div>

                    {/* 주간 라이더 테이블 */}
                    <div
                        ref={weeklyRiderTableRef}
                        className={styles['rider-table-container']}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleWeeklyTouchEnd}
                    >
                        {!ridersInitialized ? (
                            <div className={styles['pull-indicator']} style={{ height: 56 }}>
                                <div className={`${styles['pull-icon']} ${styles['pull-icon-spinning']}`}>↻</div>
                                <span className={styles['pull-text']}>불러오는 중...</span>
                            </div>
                        ) : weeklyRiders.length === 0 ? (
                            <div className={styles['rider-empty']}>
                                등록된 라이더가 없습니다.
                            </div>
                        ) : (
                            <>
                                <div className={styles['rider-table-scroll']}>
                                    <table className={styles['rider-table']}>
                                        <thead>
                                        <tr>
                                            <th>이름</th>
                                            <th>운영상태</th>
                                            <th>전화번호</th>
                                            <th>수행 건수</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {paginatedWeeklyRiders.map((rider) => (
                                            <tr
                                                key={rider.id}
                                                onClick={() => handleRiderRowClick(rider)}
                                                className={styles['rider-table-row']}
                                            >
                                                <td>
                                                    <span
                                                        className={styles['rider-name']}
                                                        onTouchStart={(e) => handleNameTouchStart(e, rider.name)}
                                                        onTouchEnd={handleNameTouchEnd}
                                                        onTouchCancel={handleNameTouchEnd}
                                                    >
                                                        {rider.name}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`${styles['rider-status-badge']} ${styles[rider.status]}`}>
                                                        {rider.status === 'active' ? '운행중' : '운행종료'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <a
                                                        href={`tel:${rider.phoneNumber}`}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className={styles['phone-link']}
                                                    >
                                                        {rider.phoneNumber}
                                                    </a>
                                                </td>
                                                <td>{rider.completedCount}건</td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>
                                {/* 페이지 인디케이터 */}
                                {totalWeeklyRiderPages > 1 && (
                                    <div className={styles['rider-pagination']}>
                                        <span className={styles['rider-pagination-text']}>
                                            {weeklyRiderPage + 1} / {totalWeeklyRiderPages}
                                        </span>
                                        <div className={styles['rider-pagination-dots']}>
                                            {getPaginationDots(weeklyRiderPage, totalWeeklyRiderPages).map(({ index, size }) => (
                                                <span
                                                    key={index}
                                                    className={`${styles['rider-pagination-dot']} ${styles[size]}`}
                                                />
                                            ))}
                                        </div>
                                        <span className={styles['rider-pagination-hint']}>← 스와이프하여 이동 →</span>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    // 라이더 상세 모달 렌더링
    const renderRiderModal = () => {
        if (!isRiderModalOpen || !selectedRider) return null;

        return (
            <div className={styles['rider-modal-overlay']} onClick={closeRiderModal}>
                <div className={styles['rider-modal']} onClick={(e) => e.stopPropagation()}>
                    <div className={styles['rider-modal-handle']} />
                    <div className={styles['rider-modal-header']}>
                        <h3 className={styles['rider-modal-title']}>라이더 상세 정보</h3>
                        <button className={styles['rider-modal-close']} onClick={closeRiderModal}>
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                    <div className={styles['rider-modal-content']}>
                        {/* 프로필 */}
                        <div className={styles['rider-modal-profile']}>
                            <div className={styles['rider-modal-avatar']}>
                                {selectedRider.name.charAt(0)}
                            </div>
                            <div className={styles['rider-modal-profile-info']}>
                                <div className={styles['rider-modal-name']}>{selectedRider.name}</div>
                                <span className={`${styles['rider-modal-status-badge']} ${styles[`status-${selectedRider.status}`]}`}>
                                    {selectedRider.status === 'active' ? '운행중' : '운행종료'}
                                </span>
                            </div>
                        </div>

                        {/* 전화 버튼 */}
                        <a
                            href={`tel:${selectedRider.phoneNumber}`}
                            className={styles['rider-modal-call-btn']}
                        >
                            <i className="fas fa-phone"></i>
                            {selectedRider.phoneNumber}
                        </a>

                        {/* 핵심 지표 */}
                        <div className={styles['rider-modal-stats']}>
                            <div className={`${styles['rider-modal-stat']} ${styles['rider-modal-stat--count']}`}>
                                <div className={styles['rider-modal-stat-value']}>
                                    {selectedRider.completedCount}<span className={styles['rider-modal-stat-unit']}>건</span>
                                </div>
                                <div className={styles['rider-modal-stat-label']}>수행 건수</div>
                            </div>
                            <div className={`${styles['rider-modal-stat']} ${styles['rider-modal-stat--rate']}`}>
                                <div className={styles['rider-modal-stat-value']}>
                                    {selectedRider.acceptanceRate}<span className={styles['rider-modal-stat-unit']}>%</span>
                                </div>
                                <div className={styles['rider-modal-stat-label']}>수락률</div>
                            </div>
                        </div>

                        {/* 오더 타입별 수행 이력 */}
                        <div className={styles['rider-modal-order-types']}>
                            <div className={styles['rider-modal-order-types-title']}>오더 타입별 수행 이력</div>
                            <div className={styles['rider-modal-order-types-grid']}>
                                <div className={`${styles['rider-modal-order-type-item']} ${styles['rider-modal-order-type--food']}`}>
                                    <div className={styles['rider-modal-order-type-label']}>푸드</div>
                                    <div className={styles['rider-modal-order-type-value']}>{selectedRider.foodCount}<span className={styles['rider-modal-order-type-unit']}>건</span></div>
                                </div>
                                <div className={`${styles['rider-modal-order-type-item']} ${styles['rider-modal-order-type--bmart']}`}>
                                    <div className={styles['rider-modal-order-type-label']}>비마트</div>
                                    <div className={styles['rider-modal-order-type-value']}>{selectedRider.bmartCount}<span className={styles['rider-modal-order-type-unit']}>건</span></div>
                                </div>
                                <div className={`${styles['rider-modal-order-type-item']} ${styles['rider-modal-order-type--store']}`}>
                                    <div className={styles['rider-modal-order-type-label']}>스토어</div>
                                    <div className={styles['rider-modal-order-type-value']}>{selectedRider.storeCount}<span className={styles['rider-modal-order-type-unit']}>건</span></div>
                                </div>
                            </div>
                        </div>

                        {/* 슬롯별 현황 */}
                        {riderSlotNames.length > 0 && (
                            <div className={styles['rider-modal-slots']}>
                                <div className={styles['rider-modal-slots-title']}>시간대별 현황</div>
                                {riderSlotNames.map((slotName, idx) => (
                                    <div key={idx} className={styles['rider-modal-slot-row']}>
                                        <span className={styles['rider-modal-slot-name']}>{slotName}</span>
                                        <span className={styles['rider-modal-slot-count']}>{selectedRider.slotCounts[idx] ?? 0}건</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // 자동 갱신 라벨 가져오기
    const getAutoRefreshLabel = () => {
        const option = autoRefreshOptions.find(opt => opt.value === autoRefreshInterval);
        return option ? option.label : '사용안함';
    };

    const goToMyPage = () => {
        navigate('/my-page');
    };

    // 점검 화면 확인 처리
    const handleMaintenanceAcknowledge = () => {
        if (activeMaintenance) {
            sessionStorage.setItem('maintenance_acknowledged', 'true');
            sessionStorage.setItem('maintenance_acknowledged_seqno', String(activeMaintenance.seqNo));
            setMaintenanceAcknowledged(true);
        }
    };


    // 점검 데이터 로딩 중이면 대기
    if (maintenanceLoading) {
        return null; // 또는 로딩 스피너
    }

    // 점검 중이고 아직 확인하지 않았으면 MaintenanceScreen 전체 화면 표시
    if (isMaintenance && activeMaintenance && !maintenanceAcknowledged) {
        return (
            <MaintenanceScreen 
                maintenance={activeMaintenance} 
                showDashboardButton={true}
                onDashboardClick={handleMaintenanceAcknowledge}
            />
        );
    }

    return (
        <div className={`screen active ${styles['supplier-home-screen']}`}>
            {/* 헤더 + 탭 고정 영역 */}
            <div className={styles['sticky-zone']}>
                {/* 헤더 */}
                <div className={styles['supplier-header']}>
                    <div className={styles['supplier-header-top']}>
                        <div className={styles['supplier-title-section']}>
                            <h1 className={styles['supplier-title']}>Vpay</h1>
                            <div className={styles['supplier-info']}>
                                <span className={styles['supplier-name']}>{supplierInfo.supplierName}</span>
                                <span className={styles['supplier-update']}>업데이트 - {lastUpdate}</span>
                            </div>
                        </div>
                        <div className={styles['supplier-header-actions']}>
                            {/*<div className={styles['auto-refresh-wrapper']}>*/}
                            {/*    <button*/}
                            {/*        className={`${styles['auto-refresh-btn']} ${autoRefreshInterval !== 'off' ? styles['active'] : ''}`}*/}
                            {/*        onClick={handleAutoRefreshToggle}*/}
                            {/*    >*/}
                            {/*        자동갱신 ({getAutoRefreshLabel()})*/}
                            {/*    </button>*/}
                            {/*    {showAutoRefreshSelect && (*/}
                            {/*        <div className={styles['auto-refresh-dropdown']}>*/}
                            {/*            {autoRefreshOptions.map((option) => (*/}
                            {/*                <button*/}
                            {/*                    key={option.value}*/}
                            {/*                    className={`${styles['auto-refresh-option']} ${autoRefreshInterval === option.value ? styles['selected'] : ''}`}*/}
                            {/*                    onClick={() => handleAutoRefreshChange(option.value as AutoRefreshInterval)}*/}
                            {/*                >*/}
                            {/*                    {option.label}*/}
                            {/*                </button>*/}
                            {/*            ))}*/}
                            {/*        </div>*/}
                            {/*    )}*/}
                            {/*</div>*/}
                            <BalanceDisplay />
                            <button className={styles['settings-btn']} onClick={goToMyPage}>
                                <i className="fas fa-cog"></i>
                            </button>
                        </div>
                    </div>
                </div>

                {/* 탭 */}
                <div className={styles['tabs-card-outer']}>
                    <div className={styles['tabs-card']}>
                        {renderTabs()}
                    </div>
                </div>
            </div>

            {/* 메인 컨텐츠 */}
            <div ref={containerRef} className={styles['supplier-container']}>
                {/* Pull-to-refresh 인디케이터 */}
                {(pullDistance > 0 || isRefreshing) && (
                    <div
                        className={styles['pull-indicator']}
                        style={{ height: isRefreshing ? 56 : pullDistance }}
                    >
                        <div
                            className={`${styles['pull-icon']} ${isRefreshing ? styles['pull-icon-spinning'] : ''}`}
                            style={{ transform: `rotate(${Math.min((pullDistance / threshold) * 180, 180)}deg)` }}
                        >
                            ↻
                        </div>
                        <span className={styles['pull-text']}>
                            {isRefreshing ? '새로고침 중...' : pullDistance >= threshold ? '놓아서 새로고침' : '당겨서 새로고침'}
                        </span>
                    </div>
                )}
                {renderDailyStatusSection()}
                {renderWeeklyScoreSection()}
            </div>

            {/* 하단 네비게이션 */}
            <BottomNavigation currentScreen="delivery" />

            {/* 라이더 상세 모달 */}
            {renderRiderModal()}

            {/* 이름 툴팁 (Long Press) */}
            {nameTooltip.visible && (
                <div
                    className={styles['name-tooltip']}
                    style={{
                        left: nameTooltip.x,
                        top: nameTooltip.y,
                    }}
                    onClick={hideNameTooltip}
                    onTouchEnd={hideNameTooltip}
                >
                    {nameTooltip.name}
                </div>
            )}

            {/* 공지사항 팝업 */}
            {popupNotices.length > 0 && popupNotices[currentNoticeIndex] && (
                <NoticePopupModal
                    notice={popupNotices[currentNoticeIndex]}
                    zIndex={1000 + (popupNotices.length - currentNoticeIndex)}
                    onClose={handleCloseNotice}
                    onHideForToday={handleHideForToday}
                />
            )}
        </div>
    );
};

export default HomePageForSupplier;
